import React from 'react';
import Joi from 'joi';
import PropTypes from 'prop-types';

import Button from '../../themed/button';
import Conditional from '../../common/conditional';
import Form from '../../common/form';

import { matchPhrases } from '../../../utils/utilities';
import { getAffirmativeOptions } from '../../../utils/data';

import toastService from '../../../services/toastService';
import withRouter from '../../hoc/withRouter';
import withModules from '../../hoc/withModules';

class LineNumberForm extends Form {
	state: any = {
		data: {
			lineNumber: '',
			hasPID: '',
			hasAnomalies: '',
			inspectionDescription: 'No anomalies observed.',
			hasPDO: '',
		},
		errors: {},
		loading: false,
		progress: 0,
	};

	schema = {
		lineNumber: Joi.object({
			label: Joi.string().required(),
			value: Joi.string().required(),
			__isNew__: Joi.boolean().optional(),
		})
			.required()
			.label('Line Number'),
		hasPID: Joi.string().min(2).required().label('Has P&ID'),
		hasPDO: Joi.string().min(2).required().label('Has PDO'),
		hasAnomalies: Joi.string().min(2).required().label('Has Anomalies'),
		inspectionDescription: Joi.string().when('hasAnomalies', {
			is: 'yes',
			then: Joi.string().allow('').optional().label('Inspection Description'),
			otherwise: Joi.string().min(3).required().label('Inspection Description'),
		}),
	};

	static propTypes = {
		inspectableItems: PropTypes.array,
		lineDetails: PropTypes.object,
		showLastLineOption: PropTypes.bool,
		onCancelEdit: PropTypes.func,
		onAdd: PropTypes.func,
	};

	componentDidMount(): void {
		if (this.props.lineDetails) {
			const lineNumber = this.createNewMultiSelectOption(this.props.lineDetails.lineNumber);
			this.setState({ data: { ...this.props.lineDetails, lineNumber } });
		}
	}

	doSubmit = (): void => {
		this.props.onAdd({
			lineNumber: this.state.data.lineNumber.value,
			hasPID: this.state.data.hasPID,
			hasPDO: this.state.data.hasPDO,
			hasAnomalies: this.state.data.hasAnomalies,
			inspectionDescription: this.state.data.inspectionDescription,
		});

		this.setState({
			data: {
				lineNumber: '',
				hasPID: '',
				hasPDO: '',
				hasAnomalies: '',
				inspectionDescription: 'No anomalies observed.',
			},
		});

		toastService.success('Line Item Saved Successfully!');
	};

	getPIDOptions = () => {
		if (this.props.showLastLineOption) {
			return [...getAffirmativeOptions(), { id: 'same as last line', name: 'Same as last Line' }];
		}

		return getAffirmativeOptions();
	};

	createNewMultiSelectOption = (lineNumber: string) => {
		return { label: lineNumber, value: lineNumber };
	};

	handleNewOptionCreation = (newLineNumber: string) => {
		const newLineNumberOption = this.createNewMultiSelectOption(newLineNumber);
		const data = { ...this.state.data, lineNumber: newLineNumberOption };

		this.setState({ data });
	};

	render() {
		return (
			<form autoComplete="off" onSubmit={this.handleSubmit}>
				<div className="row">
					<div className="col">
						{this.renderCreatableMultiselect({
							closeMenuOnSelect: true,
							options: this.props.inspectableItems,
							name: 'lineNumber',
							placeholder: 'Enter Line Number',
							label: 'Line Number',
							isMulti: false,
							onCreateOption: this.handleNewOptionCreation
						})}
					</div>

					<div className="col">
						{this.renderSelect({
							name: 'hasPID',
							placeholder: 'Does this line have a P&ID?',
							label: 'P&ID:',
							options: this.getPIDOptions(),
						})}
					</div>
				</div>

				<div className="my-4 divider"></div>

				<div className="row">
					<div className="col">
						{this.renderSelect({
							name: 'hasAnomalies',
							placeholder: 'Does this line have any anomalies?',
							label: 'Has Anomalies:',
							options: getAffirmativeOptions(),
						})}
					</div>
					
					<div className="col">
						{this.renderSelect({
							name: 'hasPDO',
							placeholder: 'Does this line have any PDO?',
							label: 'Has PDO:',
							options: getAffirmativeOptions(),
						})}
					</div>
				</div>

				<div className="my-4 divider"></div>

				<Conditional isVisible={matchPhrases(this.state.data.hasAnomalies, 'no')}>
					{this.renderTextArea({
						name: 'inspectionDescription',
						placeholder: 'Enter Inspection Description',
						label: 'Inspection Description',
						rows: '6',
					})}

					<div className="my-4 divider"></div>
				</Conditional>

				<div className="d-flex justify-content-end">
					{this.renderButton('Save And Proceed')}

					<Conditional isVisible={!!this.props.onCancelEdit}>
						<div className="mx-4">
							<Button
								label="Cancel"
								style={{ backgroundColor: 'red' }}
								onClick={this.props.onCancelEdit}
							/>
						</div>
					</Conditional>
				</div>
			</form>
		);
	}
}

const LineNumberWithRouter = withRouter(LineNumberForm);
export default React.memo(withModules(LineNumberWithRouter));
